import { SnackbarEvents } from "@gtmhub/shared/components/ui/snackbar/snackbar.events";
import { ITimeoutService, IQService, IPromise } from "angular";
import { StateService, TransitionService } from "@uirouter/angularjs";
import { IModalService, IModalScope } from "angular-ui-bootstrap";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { canCascadeMetric, CascadingMetricFormModal, DisableMetricSyncModal, IMetric, MetricLinkService, MetricService } from "@gtmhub/okrs/metrics";
import { IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/goalService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalModalsService } from "@gtmhub/goals/goalModalsService";
import { IUser } from "@gtmhub/users/models";
import { IAssignee, IAssigneeIdMap } from "@gtmhub/assignees/models";
import { animationDurationTimeout, IAlignmentTreeRenderer, alignmentTreeRendererFactory } from "../renderer/alignment-tree-renderer";
import { IItemsAlignmentData, IItemsTreeAdditionalData, ITreeContext, IAlignmentTreeFilterResult, IAlignmentTreeGoal, IAlignmentTreeMetric } from "../models";
import { IGoalsMetricsData } from "@gtmhub/sockets";
import { IGoalsScope } from "@gtmhub/goals/controllers/goals";
import { IStateInit } from "@gtmhub/core/routing";
import { itemEquals } from "../renderer/util";
import { AdditionalOwnersOpenEventDatum, IItem, IItemId, IItemRearrangeEventArgs, TreeViewMode } from "../renderer/models";
import { ItemsTreeService } from "../items-tree-service";
import { AlignmentLoader, IAlignmentLoadedEventArgs, AlignmentTreeViewMode, AlignmentContentState } from "../alignmentLoader";
import { OkrEvents, GoalsChangedEventArgs, CloneOkrsModal } from "@gtmhub/okrs";
import { isCircularDependency, resetPersistedExpandedItems, locatedItemOverlayAnimation, getPersistedExpandedGoals, getPersistedExpandedSelfIds } from "../utils";
import { AccountService } from "@gtmhub/accounts/accountsService";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { AccountType, GtmhubController, IAccount, IRestLayerRequest } from "@gtmhub/core";
import {
  IGridSortingRule,
  toRestLayerRequest,
  IGridFilter,
  hasFilterValue,
  expandFilterValue,
  IArrayGridFilter,
  IGoalsSharedViewOptions,
  setPersistedScreenSettings,
  getPersistedScreenSettings,
  RestLayerFilterFactory,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { copyToClipboard } from "@gtmhub/shared/dom";
import { allowedActionsSync } from "@gtmhub/permissions/utils";
import { IAllowedActionsMap, ISessionsIdMap } from "@gtmhub/sessions";
import {
  hasOnlyHostSessionFilter,
  hasFiltersWithValues,
  hasOnlySessionFilter,
  goalsViewOptionsPath,
  filtersOverrideForTagTitle,
  separateDynamicValues,
  filtersOverrideForOkrName,
  expandedGoalsScreenPath,
} from "@gtmhub/goals/filter-utils";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { MISSING_SESSION_ID_STATE_PARAM } from "@gtmhub/goals/controllers/all";
import { addToArrayIfMissing, IdMap } from "@gtmhub/util";
import { IGoalChildReorderedEventArgs } from "@gtmhub/okrs/events";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { NAV_WIDTH } from "@webapp/navigation/components/navigation/config";
import { isOkrStatusAbandoned } from "@gtmhub/goals/utils";
import { getCopyURLNotificationEl } from "@gtmhub/shared/utils/dom-utils";
import { byNumericField } from "@gtmhub/util/sorting";
import { OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";

type GoalAction = keyof IAllowedActionsMap;

function isDemoAccount(account: IAccount): boolean {
  return account.type === AccountType.DemoAccount;
}
interface ITreeRootScope extends ITraceRootScopeService {
  aggregation: boolean;
}

interface IConfirmDeleteMetricScope extends IModalScope {
  indicators: {
    deleting: boolean;
  };
  confirm(): void;
}

interface IBreakMetricLinksWarningScope extends IModalScope {
  metrics: IAlignmentTreeMetric[];
  confirm(): void;
}

interface IConfirmDeleteGoalScope extends IModalScope {
  linkedMetrics: IAlignmentTreeMetric[];
  confirm(): void;
}

export interface ITreeScope extends IGoalsScope {
  indicators: {
    updating: boolean;
    loading: boolean;
  };
  hasItems: boolean;
  pinnedItem: { id: string; title: string };
  viewMode: TreeViewMode;
  contextMenuItem?: IItem;
  additionalOwners: IAssignee[];
  useScrollbars: boolean;
  shareableScreenShown: boolean;
  zoomRatio: number;
  showNoFilterResults: boolean;
  showEmptyBlock: boolean;
  zoomIn(): void;
  zoomOut(): void;
  resetZoom(): void;
  unlink(item: IItem): void;
  openCascadeMetricForm(item: IItem): void;
  canCascadeMetric(item: IItem): boolean;
  isGoalAbandoned(item: IItem): boolean;
  isContributingMetricItem(item: IItem): boolean;
  confirmDeleteMetric(goalId: string, metricId: string): void;
  confirmDeleteGoal(goalId: string): void;
  openCloneModal(goalId: string): void;
  openMoveModal(goalId: string): void;
  getShareableUrl(goalId: string): void;
  closeShareableScreen(): void;
  makeRoot(goalId: string): void;
  isGoalActionAllowed(goalId: string, action: GoalAction): boolean;
  isAnyGoalActionAllowed(goalId: string, ...actions: GoalAction[]): boolean;
  isAllowedToMakeTopLevelObjective(selectedGoal: IItem): boolean;
  pin(goalId: string): void;
  unpin(): void;
  moveHere(itemId: string): void;
  addChildObjective(item: IItem): void;
  repositionTree(): void;
  switchScrollMode(useScrollbars: boolean): void;
  openCloneMetricModal(metricId: string): void;
  openDisableSyncModal(metricId: string): void;
}

const ratioStep = 0.2;

const allowedZoomRange = {
  max: 1.2,
  min: 0.2,
};

const isContributingMetricItem = (item: IItem) => item.type === "metric" && item.crossContributorLinks.some((link) => link.direction === "outbound");

const isContributingMetric = (metric: IAlignmentTreeMetric): boolean =>
  metric && metric.links && metric.links.spec && metric.links.spec.contributing && metric.links.spec.contributing.length > 0;

const isCascadingMetric = (metric: IAlignmentTreeMetric): boolean =>
  metric && metric.links && metric.links.spec && metric.links.spec.cascading && metric.links.spec.cascading.length > 0;

export const getGoalsForSessionsFields =
  "id,metrics{id,name,attainment,ownerIds,goalId,actual,format,links{spec},cascadeType,sourceMetricId,orderId,targetOperator,initialValue,target,fieldName,insightName,dynamic,sessionId},parentId,parentType,ownerIds," +
  "name,attainment,attainmentTypeString,fullAggregatedAttainment,sessionId,obfuscated,private,locked,workflow{status},orderId,closedStatus,designScore{totalPoints}";

export class AlignmentTreeCtrl extends GtmhubController implements IStateInit {
  private treeRenderer?: IAlignmentTreeRenderer;
  private currentUser: IUser;
  private selectedSessionIds: string[];
  private currentSessionId: string;
  private sharedId?: string;
  private sharedParentId?: string;
  private locateId: string;
  private sessionIdMap: ISessionsIdMap;
  private order: IGridSortingRule[];
  private assigneesIdMap: IAssigneeIdMap;
  private itemsData: IItemsAlignmentData;
  private goalsById: { [goalId: string]: IAlignmentTreeGoal } = {};
  private goalIdsByParent: { [parentId: string]: string[] } = {};
  private goalIdsBySession: { [sessionId: string]: string[] } = {};
  private allowedActionsByGoal: { [goalId: string]: string[] } = {};
  private enabledDrag: boolean;
  private pinnedItem: IItem;
  private okrEvents: OkrEvents;
  private filters: IGridFilter[];
  private contentState: AlignmentContentState;
  private isReorderGoalChildrenFeatureEnabled: boolean;
  private isCloseOkrFeatureEnabled: boolean;
  private isShareableURLsFeatureEnabled: boolean;
  private isMetricCascadeTypeFeatureEnabled: boolean;
  private snackbarEvents: SnackbarEvents;
  private isNestedViewV2Enabled: boolean;

  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$transitions",
    "$timeout",
    "$q",
    "$uibModal",
    "GoalService",
    "ItemsTreeService",
    "MetricService",
    "UIErrorHandlingService",
    "MetricLinkService",
    "GoalModalsService",
    "AlignmentLoader",
    "AccountResolverService",
    "AccountService",
    "AnalyticsService",
    "CloneOkrsModal",
    "DisableMetricSyncModal",
    "FeatureToggleV2Service",
    "CascadingMetricFormModal",
    "OkrMethodologyFacade",
  ];

  constructor(
    private $rootScope: ITreeRootScope,
    private $scope: ITreeScope,
    private $state: StateService,
    private $transitions: TransitionService,
    private $timeout: ITimeoutService,
    private $q: IQService,
    private $uibModal: IModalService,
    private goalService: GoalService,
    private itemsTreeService: ItemsTreeService,
    private metricService: MetricService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private metricLinkService: MetricLinkService,
    private goalModalsService: GoalModalsService,
    private alignmentLoader: AlignmentLoader,
    private accountResolverService: AccountResolverService,
    private accountService: AccountService,
    private analyticsService: AnalyticsService,
    private cloneOkrsModal: CloneOkrsModal,
    private disableMetricSyncModal: DisableMetricSyncModal,
    private featureToggleService: FeatureToggleV2Service,
    private cascadingMetricFormModal: CascadingMetricFormModal,
    private okrMethodologyFacade: OkrMethodologyFacade
  ) {
    super();

    this.okrEvents = new OkrEvents($scope);
    this.snackbarEvents = new SnackbarEvents($scope);

    this.$rootScope.noNavigation = false;

    this.$scope.indicators = {
      updating: false,
      loading: true,
    };
    this.$scope.zoomRatio = 1;

    this.$scope.zoomIn = this.zoomIn;
    this.$scope.zoomOut = this.zoomOut;
    this.$scope.resetZoom = this.resetZoom;
    this.$scope.unlink = this.unlink;
    this.$scope.openCascadeMetricForm = this.openCascadeMetricForm;
    this.$scope.canCascadeMetric = this.canCascadeMetric;
    this.$scope.isGoalAbandoned = this.isGoalAbandoned;
    this.$scope.isContributingMetricItem = isContributingMetricItem;
    this.$scope.confirmDeleteMetric = this.confirmDeleteMetric;
    this.$scope.confirmDeleteGoal = this.confirmDeleteGoal;
    this.$scope.openCloneModal = this.openCloneModal;
    this.$scope.openMoveModal = this.openMoveModal;
    this.$scope.getShareableUrl = this.getShareableUrl;
    this.$scope.closeShareableScreen = this.closeShareableScreen;
    this.$scope.makeRoot = this.makeRoot;
    this.$scope.isGoalActionAllowed = this.isGoalActionAllowed;
    this.$scope.isAnyGoalActionAllowed = this.isAnyGoalActionAllowed;
    this.$scope.pin = this.pin;
    this.$scope.unpin = this.unpin;
    this.$scope.moveHere = this.moveHere;
    this.$scope.addChildObjective = this.addChildObjective;
    this.$scope.repositionTree = this.repositionTree;
    this.$scope.switchScrollMode = this.switchScrollMode;
    this.$scope.openCloneMetricModal = this.openCloneMetricModal;
    this.$scope.openDisableSyncModal = this.openDisableSyncModal;
    this.$scope.isAllowedToMakeTopLevelObjective = this.isAllowedToMakeTopLevelObjective;

    this.openWelcomeModal();

    const closeContextMenu = this.closeContextMenu.bind(this);
    document.body.addEventListener("click", closeContextMenu);
    this.$scope.$on("$destroy", () => document.body.removeEventListener("click", closeContextMenu));
  }

  public stateInit(): IPromise<unknown> {
    return this.alignmentLoader.promise().then((args) => {
      return this.onLoaded(args);
    });
  }

  private onLoaded = (args: IAlignmentLoadedEventArgs): IPromise<unknown> => {
    return this.$rootScope.traceAction("alignment_tree_init", () => {
      this.initScopeListeners();

      return this.init(args);
    });
  };

  private initScopeListeners = (): void => {
    this.$scope.$on("alignment.expandAll", () => this.expandAll());
    this.$scope.$on("alignment.collapseAll", () => this.collapseAll());
    this.$scope.$on("alignment.viewModeSwitched", (evt, viewMode: AlignmentTreeViewMode) => this.switchViewMode(viewMode));
    this.$scope.$on("alignment.dragModeChanged", (evt, enabledDrag: boolean) => this.switchDragMode(enabledDrag));
    this.$scope.$on("alignment.orderChanged", (evt, orderRules: Record<"order", IGridSortingRule[]>) => this.orderChanged(orderRules.order));
    this.$scope.$on("wsGoalsAndMetricsChanged", (evt, wsData: IGoalsMetricsData) => this.applyWSChanges(wsData));
    // TODO: Delete when release use-indirect-metric-cascade-type
    this.$scope.$on("sliceAdded", (evt, args: { cascading: IMetric; contributing: IMetric }) => this.addSlice(args));
    this.$scope.$on("alignment.unifiedFiltersChanged", (evt, args: IAlignmentLoadedEventArgs) => this.applyUnifiedFiltersChanges(args));
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));
  };

  private initTreeRenderer = (): void => {
    const account = this.accountResolverService.getAccountData();
    const isDemo = isDemoAccount(account);
    const sessionSettings = this.sessionIdMap[this.currentSessionId]?.settings;
    const isProgressAggregationEnabled = !this.okrMethodologyFacade.isProgressAggregationDisabled(sessionSettings);
    const treeRendererOptions = isDemo ? { x: 40, y: 50 } : null;

    this.treeRenderer = alignmentTreeRendererFactory.create(document.getElementById("alignment-tree-svg-holder"), treeRendererOptions);

    this.treeRenderer
      .setItemRearrangeEnabled(this.enabledDrag)
      .setZoomEnabled(!this.$scope.useScrollbars)
      .setZoomRange(allowedZoomRange)
      .setViewMode(this.$scope.viewMode)
      .setAggregatedAttainmentVisibility(isProgressAggregationEnabled)
      .setContextMenuVisibility(
        (item) =>
          !item.parent ||
          item.parent.relation === "support" ||
          this.$rootScope.hasPermission("ManageGoals") ||
          (this.isShareableURLsFeatureEnabled && this.$rootScope.hasPermission("AccessGoals"))
      )
      .setItemRearrangeAllowed({
        start: (item) => (item.type !== "goal" || this.canHandleGoalItem(item)) && (!item.parent || item.parent.relation !== "contribution"),
        end: (item: IItem, newParent: IItem) => {
          const isSameItem = itemEquals(item, newParent);
          const isSameParent = item.parent && itemEquals(item.parent, newParent);
          const isCircular = isCircularDependency(item, newParent, this.itemsData.idMap);
          const isPrivateObfuscatedItem = newParent.private && newParent.obfuscated;
          return !(isSameItem || isSameParent || isPrivateObfuscatedItem || isCircular);
        },
      });

    this.treeRenderer.on("contextMenuOpen", (evt: MouseEvent, item: IItem) => this.contextMenuOpen(evt, item));
    this.treeRenderer.on(
      "additionalOwnersOpen",
      (evt: MouseEvent, datum: AdditionalOwnersOpenEventDatum) => !this.$scope.additionalOwners && this.additionalOwnersOpen(evt, datum)
    );
    this.treeRenderer.on("additionalOwnersClose", () => this.$scope.additionalOwners && this.closeAdditionalOwnersMenu());
    this.treeRenderer.on("itemTitleClick", (evt, item: IItem) => this.itemTitleClick(item));
    this.treeRenderer.on("itemRearrange", (evt, args: IItemRearrangeEventArgs) => this.itemRearrange(args));
    this.treeRenderer.on("toggleItemSupporters", (evt, item: IItem) => this.toggleItemSupporters(item));
    this.treeRenderer.on("toggleItemSelf", (evt, item: IItem) => this.toggleItemSelf(item));
    this.treeRenderer.on("showMoreClick", (evt, parent: IItem | null) => this.showHiddenChildItems(parent));
    this.treeRenderer.on("zoom", () => {
      this.$scope.contextMenuItem && this.closeContextMenu();
      this.$scope.additionalOwners && this.closeAdditionalOwnersMenu();
    });

    this.$scope.$on("$destroy", () => this.treeRenderer.destroy());
  };

  private init = (args: IAlignmentLoadedEventArgs): IPromise<unknown> => {
    if (args.viewModes.nested) {
      return this.$state.go("gtmhub.goals.alignmentSidebar.nested", null, { location: "replace" });
    }

    this.evaluateCtrlVariables(args);
    this.setUseScrollbars();
    this.initTreeRenderer();
    return this.initPreselectedSessions();
  };

  private applyUnifiedFiltersChanges = (args: IAlignmentLoadedEventArgs) => {
    if (this.locateId) {
      // if there is an item already located, due to animation stroke color's value is in inline style -> remove it so class rules can be applied
      const item: IItemId = { id: this.locateId, type: "goal" };
      this.treeRenderer.setStyle(item, { childSelector: "rect.pad", css: { property: "stroke", value: null } });
    }
    // to do: at later stage test and handle if needed(args.viewMode === "nested") similar to init
    this.evaluateCtrlVariables(args);
    resetPersistedExpandedItems(this.currentSessionId);

    this.goalsById = {};
    this.allowedActionsByGoal = {};
    this.goalIdsBySession = {};
    this.goalIdsByParent = {};

    // the method below forces scrolling to the top match if any nodes are matching filters or to the first node after tree construction
    this.initPreselectedSessions();
  };

  private evaluateCtrlVariables = (args: IAlignmentLoadedEventArgs): void => {
    this.assigneesIdMap = args.assigneesIdMap;
    this.$scope.viewMode = args.viewModes.showMetrics ? "relaxed" : "medium";
    this.enabledDrag = args.enabledDrag;
    this.currentUser = args.currentUser;
    this.sessionIdMap = args.sessionIdMap;
    this.sharedId = args.sharedId;
    this.sharedParentId = args.sharedParentId;
    this.locateId = args.locateId;
    this.currentSessionId = args.currentSessionId;

    const sessionFilter = args.filters.find((f) => f.fieldName === "sessionId");
    if (sessionFilter && hasFilterValue(sessionFilter)) {
      this.selectedSessionIds = expandFilterValue(<IArrayGridFilter>sessionFilter).filter((sessionId) => sessionId !== MISSING_SESSION_ID_STATE_PARAM);
    } else {
      this.selectedSessionIds = [this.currentSessionId];
    }
    this.order = args.order;
    this.filters = args.filters;
    this.contentState = args.contentState;
  };

  private setUseScrollbars = (): void => {
    const persistedViewOptions: IGoalsSharedViewOptions = getPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions") || {};
    if (persistedViewOptions) {
      this.$scope.useScrollbars = !!persistedViewOptions.useScrollbars;
    } else {
      this.$scope.useScrollbars = false;
    }
  };
  private setEmptyScreenAndNoFilterDisplay = (): void => {
    this.$scope.showEmptyBlock = !this.$scope.hasItems && this.contentState.noFiltersOrHostSessionOnly;
    this.$scope.showNoFilterResults = !this.$scope.hasItems && hasFiltersWithValues(this.filters) && !hasOnlyHostSessionFilter(this.filters, this.currentSessionId);
  };

  private isAllowedToMakeTopLevelObjective = (item: IItem): boolean => {
    if (item?.parent?.type === "metric") {
      const foundMetric = this.itemsData.idMap[item.parent.id];
      const foundParentGoal = this.itemsData.idMap[foundMetric?.id];

      if (foundParentGoal?.parent) {
        return true;
      }
    }

    if (item?.parent?.type === "goal") {
      const foundParentGoal = this.goalsById[item.parent.id];

      if (foundParentGoal) {
        return true;
      }
    }

    return false;
  };

  private initPreselectedSessions = (): IPromise<unknown> => {
    return this.$q
      .all({
        goals: this.getGoalsForSessions(this.selectedSessionIds),
        isReorderGoalChildrenFeatureEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.FeatureReorderGoalChildren),
        isCloseOkrFeatureEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.FeatureCloseOKR),
        isShareableURLsFeatureEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.OKRsShareableURLs),
        isNestedViewV2Enabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.NestedViewV2),
        isMetricCascadeTypeFeatureEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.UseIndirectMetricCascadeType),
      })
      .then(
        ({
          goals,
          isReorderGoalChildrenFeatureEnabled,
          isCloseOkrFeatureEnabled,
          isShareableURLsFeatureEnabled,
          isNestedViewV2Enabled,
          isMetricCascadeTypeFeatureEnabled,
        }) => {
          this.isReorderGoalChildrenFeatureEnabled = isReorderGoalChildrenFeatureEnabled;
          this.isCloseOkrFeatureEnabled = isCloseOkrFeatureEnabled;
          this.isShareableURLsFeatureEnabled = isShareableURLsFeatureEnabled;
          this.$scope.hasItems = goals.length > 0;
          this.contentState.hasItems = this.$scope.hasItems;
          this.contentState.noFiltersOrHostSessionOnly = !hasFiltersWithValues(this.filters) || hasOnlyHostSessionFilter(this.filters, this.currentSessionId);
          this.setEmptyScreenAndNoFilterDisplay();
          this.addGoals(...goals);
          this.isNestedViewV2Enabled = isNestedViewV2Enabled;
          this.isMetricCascadeTypeFeatureEnabled = isMetricCascadeTypeFeatureEnabled;

          return this.constructTreeAndRender().then(() => {
            if (this.sharedId) {
              this.centerTreeToSharedItem();
            } else if (this.locateId) {
              this.locateItem();
            } else {
              // if there are filters applied and any nodes matching filters -> scroll to the top match
              this.scrollToTopMatchOrTreeBasis();
            }

            this.analyticsService.track("Alignment Tree Data Loaded", this.generateAlignmentTreeMetadata(goals));
          });
        }
      )
      .catch((error) => this.uiErrorHandlingService.handleModal(error))
      .finally(() => (this.$scope.indicators.loading = false));
  };

  private addGoals = (...goals: IAlignmentTreeGoal[]): void => {
    for (const goal of goals) {
      const goalId = goal.id;
      const existingGoal = this.goalsById[goalId];

      this.goalsById[goalId] = goal;
      this.$scope.hasItems = Object.keys(this.goalsById).length > 0;
      this.allowedActionsByGoal[goalId] = allowedActionsSync(this.currentUser, this.sessionIdMap[goal.sessionId].access);

      // the goal may exist with different session, so we remove the goal from this mapping first
      if (existingGoal && this.goalIdsBySession[existingGoal.sessionId]) {
        this.goalIdsBySession[existingGoal.sessionId] = this.goalIdsBySession[existingGoal.sessionId].filter((id) => id !== goalId);
      }

      // add the goal to the session mapping
      this.goalIdsBySession[goal.sessionId] = addToArrayIfMissing(goalId, this.goalIdsBySession[goal.sessionId] || []);

      // the goal may exist with a different parent, so we remove the goal from this mapping first
      if (existingGoal && existingGoal.parentId && this.goalIdsByParent[existingGoal.parentId]) {
        this.goalIdsByParent[existingGoal.parentId] = this.goalIdsByParent[existingGoal.parentId].filter((id) => id !== goalId);
      }

      // add the goal to the parent mapping
      if (goal.parentId) {
        this.goalIdsByParent[goal.parentId] = addToArrayIfMissing(goalId, this.goalIdsByParent[goal.parentId] || []);
      }
    }

    this.contentState.hasItems = this.$scope.hasItems;
    this.setEmptyScreenAndNoFilterDisplay();
  };

  private removeGoals = (...goalIds: string[]): void => {
    for (const goalId of goalIds) {
      const goal = this.goalsById[goalId];
      delete this.goalsById[goalId];
      this.$scope.hasItems = Object.keys(this.goalsById).length > 0;

      delete this.allowedActionsByGoal[goalId];

      this.goalIdsBySession[goal.sessionId] = this.goalIdsBySession[goal.sessionId].filter((id) => id !== goalId);

      if (goal.parentId && this.goalIdsByParent[goal.parentId]) {
        this.goalIdsByParent[goal.parentId] = this.goalIdsByParent[goal.parentId].filter((id) => id !== goal.id);
      }
    }

    this.contentState.hasItems = this.$scope.hasItems;
    this.setEmptyScreenAndNoFilterDisplay();
  };

  private generateAdditionalData = (): IItemsTreeAdditionalData => ({
    currentSessionId: this.currentSessionId,
    order: this.order,
    assigneesIdMap: this.assigneesIdMap,
    sessionIdMap: this.sessionIdMap,
    metricsIdMap: this.generateMetricsIdMap(),
  });

  private generateMetricsIdMap = (): IdMap<IAlignmentTreeMetric> => {
    if (!this.goalsById) {
      return {};
    }
    const idMap = {};
    Object.values(this.goalsById).forEach((goal) => {
      if (!goal.metrics) {
        return;
      }
      goal.metrics.forEach((metric) => {
        idMap[metric.id] = metric;
      });
    });
    return idMap;
  };

  private generateAlignmentTreeMetadata = (goals: IAlignmentTreeGoal[]): { objectives: number; krs: number } => ({
    objectives: goals.length,
    krs: goals.reduce((krsCount, goal) => (krsCount += (goal.metrics || []).length), 0),
  });

  private expandAll = (): void => {
    this.$rootScope.traceAction("expand_all", () => {
      this.itemsTreeService.expandAllItems(this.itemsData, this.currentSessionId);
      this.renderTree();
    });
  };

  private collapseAll = (): void => {
    this.$rootScope.traceAction("collapse_all", () => {
      this.itemsTreeService.collapseAllItems(this.itemsData, this.currentSessionId);
      this.renderTree();
    });
  };

  private scrollToTopMatchOrTreeBasis = (): void => {
    this.$timeout(() => this.treeRenderer.resetPosition({ shouldCenterFiltered: true }), animationDurationTimeout);
  };

  private orderChanged = (order: IGridSortingRule[]): void => {
    this.order = order;

    this.itemsData.tree = this.itemsTreeService.sortItemsTree(
      this.itemsData.tree,
      this.generateAdditionalData(),
      this.isReorderGoalChildrenFeatureEnabled,
      this.isNestedViewV2Enabled
    );
    this.renderTree();
  };

  private switchViewMode = (viewMode: AlignmentTreeViewMode): void => {
    this.$rootScope.traceAction("switch_view_mode", () => {
      if (viewMode === "nested") {
        return;
      }

      this.$scope.viewMode = viewMode;

      if (this.$scope.useScrollbars) {
        this.$scope.zoomRatio = 1;
      }

      this.treeRenderer.setViewMode(this.$scope.viewMode).render(this.itemsData.tree, this.treeContext);
    });
  };

  private switchScrollMode = (useScrollbars: boolean): void => {
    this.$rootScope.traceAction("switch_scroll_mode", () => {
      this.$scope.zoomRatio = 1;
      this.$scope.useScrollbars = useScrollbars;
      const gridViewOptions: IGoalsSharedViewOptions = getPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions") || {};
      gridViewOptions.useScrollbars = this.$scope.useScrollbars;
      setPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions", gridViewOptions);
      this.treeRenderer.setZoomEnabled(!useScrollbars).setZoomRatio(1).refresh();
    });
  };

  private repositionTree = (): void => {
    this.$rootScope.traceAction("reposition_tree", () => {
      this.$scope.zoomRatio = 1;
      this.treeRenderer.setZoomRatio(1).resetPosition();
    });
  };

  private switchDragMode = (enabledDrag: boolean): void => {
    this.$rootScope.traceAction("switch_drag_mode", () => {
      this.enabledDrag = enabledDrag;
      this.treeRenderer.setItemRearrangeEnabled(enabledDrag).refresh();
    });
  };

  private onGoalChildReordered = (args: IGoalChildReorderedEventArgs) => {
    if (args.detail.childType === "goal") {
      const existingGoal = this.goalsById[args.detail.childId];
      if (!existingGoal) return;
      this.addGoals({ ...existingGoal, orderId: args.detail.childOrderId });
      this.constructTreeAndRenderAndShowSnackbar({ action: args.reason, goalId: existingGoal.id }).catch((error) => this.uiErrorHandlingService.handleModal(error));
      return;
    }

    const existingParentGoal = this.goalsById[args.detail.goalId];
    if (!existingParentGoal) return;
    existingParentGoal.metrics = (existingParentGoal.metrics || []).map((metric) =>
      metric.id === args.detail.childId
        ? {
            ...metric,
            orderId: args.detail.childOrderId,
          }
        : metric
    );

    this.constructTreeAndRenderAndShowSnackbar({ action: args.reason, goalId: args.detail.goalId, metricId: args.detail.childId }).catch((error) =>
      this.uiErrorHandlingService.handleModal(error)
    );
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    this.$rootScope.traceAction(`on_goals_changed_${args.reason}`, () => {
      switch (args.reason) {
        case "goalPatched":
        case "goalUpdated":
        case "goalCreated":
        case "goalCloned":
        case "goalMoved": {
          const goal = angular.copy(args.detail.goal);
          // the goal exists, but it now belongs to some other session, which is not one of the selected ones
          if (this.goalsById[goal.id] && !this.selectedSessionIds.includes(goal.sessionId)) {
            this.removeGoals(goal.id);
          } else if (this.selectedSessionIds.includes(goal.sessionId)) {
            this.addGoals(goal as IAlignmentTreeGoal);
          }

          if (args.reason === "goalCreated") {
            const parentId = goal.parentId;

            const persistedExpandedGoals: IdMap<boolean> = getPersistedExpandedGoals(this.currentSessionId);
            persistedExpandedGoals[parentId] = true;

            const screenPath = expandedGoalsScreenPath(this.currentSessionId);
            setPersistedScreenSettings(screenPath, "expandedGoals", persistedExpandedGoals);

            const persistedExpandedSelfIds = getPersistedExpandedSelfIds(this.currentSessionId);
            persistedExpandedSelfIds[parentId] = true;
            setPersistedScreenSettings(screenPath, "expandedSelfIds", persistedExpandedSelfIds);
          }

          this.constructTreeAndRenderAndShowSnackbar({ action: args.reason, goalId: args.detail.goal.id }).catch((error) =>
            this.uiErrorHandlingService.handleModal(error)
          );
          break;
        }

        case "goalDeleted":
          this.removeGoals(args.detail.goalId);

          // Check visibility before updating the tree because the goal will be removed from the map
          if (hasFiltersWithValues(this.filters) && !hasOnlySessionFilter(this.filters)) {
            this.showAlignmentSnackbarNotification({ action: args.reason, goalId: args.detail.goalId });
          }

          this.constructTreeAndRender().catch((error) => this.uiErrorHandlingService.handleModal(error));
          break;

        case "goalChildReordered": {
          this.onGoalChildReordered(args);
          break;
        }
        case "metricDeleted":
        case "metricMoved":
        case "metricPatched":
        case "metricCreated": {
          const goal = this.goalsById[args.detail.goalId];
          if (!goal) {
            return;
          }

          if (!goal.metrics) {
            goal.metrics = [];
          }

          let metricId: string;
          if ("metric" in args.detail && args.detail.goalId === args.detail.metric.goalId) {
            const copyMetric = angular.copy(args.detail.metric);
            metricId = copyMetric.id;
            goal.metrics = goal.metrics.filter((metric) => metric.id !== copyMetric.id).concat(copyMetric as IAlignmentTreeMetric);
          } else {
            metricId = "metric" in args.detail ? args.detail.metric.id : args.detail.metricId;

            this.filterContributedMetric(goal, metricId);
            goal.metrics = goal.metrics.filter((metric) => metric.id !== metricId);
          }
          this.constructTreeAndRenderAndShowSnackbar({ action: args.reason, goalId: args.detail.goalId, metricId }).catch((error) =>
            this.uiErrorHandlingService.handleModal(error)
          );
          break;
        }

        case "tagAdded":
        case "tagRemoved":
          // don't care for tags since they are not shown here
          break;

        case "goalLinked":
        case "goalUnlinked":
          // don't care for goal links since they are only used within the goal form
          break;

        case "metricSnapshotDeleted":
        case "metricCheckin":
          // don't care for snapshots since metric value and attainment will be updated via Web Socket
          break;

        case "reviewApproved":
        case "reviewDeclined":
        case "reviewSentForApproval":
        case "reviewWithdrawn":
        case "metricUnlinked":
          this.goalService
            .getGoal(args.detail.goalId)
            .then((goal) => {
              this.addGoals(<IAlignmentTreeGoal>goal);
              return this.constructTreeAndRenderAndShowSnackbar({ action: args.reason, goalId: args.detail.goalId });
            })
            .catch((error) => this.uiErrorHandlingService.handleModal(error));
          break;

        case "metricLinked": {
          this.$q
            .all({
              goal: this.goalService.getGoal(args.detail.goalId),
              targetMetric: this.metricService.getMetric(args.detail.link.targetId),
            })
            .then(({ goal, targetMetric }) => {
              const contributingMetricParentGoal = this.goalsById[targetMetric.goalId] || ({} as IAlignmentTreeGoal);
              if (!contributingMetricParentGoal.metrics) {
                contributingMetricParentGoal.metrics = [];
              }

              const contributingMetric = contributingMetricParentGoal.metrics.find((m) => m.id === targetMetric.id);
              if (!contributingMetric) {
                contributingMetricParentGoal.metrics.push(<IAlignmentTreeMetric>targetMetric);
                contributingMetricParentGoal.sessionId = targetMetric.sessionId;
                contributingMetricParentGoal.name = targetMetric.goalName;
              } else {
                contributingMetric.links = targetMetric.links;
              }

              this.addGoals(<IAlignmentTreeGoal>goal, contributingMetricParentGoal);
              return this.constructTreeAndRenderAndShowSnackbar({ action: args.reason, goalId: args.detail.goalId });
            })
            .catch((error) => this.uiErrorHandlingService.handleModal(error));
          break;
        }
      }
    });
  };

  private filterContributedMetric = (goal: IAlignmentTreeGoal, deletedMetricId: string): void => {
    const deletedMetric = goal.metrics.find((metric) => metric.id === deletedMetricId);

    if (deletedMetric?.links?.spec?.contributing?.length) {
      const contributedMetricId = deletedMetric.links.spec.contributing[0].id;
      const parentGoal = this.goalsById[goal.parentId];
      const contributedMetric = parentGoal
        ? parentGoal.metrics.find((metric) => metric.id === contributedMetricId)
        : this.generateAdditionalData().metricsIdMap[contributedMetricId];

      if (contributedMetric?.links?.spec?.cascading.length) {
        contributedMetric.links.spec.cascading = contributedMetric.links.spec.cascading.filter((metric) => metric.id !== deletedMetricId);
      }
    }
  };

  private openWelcomeModal = (): void => {
    const account = this.accountResolverService.getAccountData();

    if (isDemoAccount(account)) {
      const onboarded = this.accountService.getAccountSetting("onboarded");

      if (!onboarded) {
        this.$uibModal
          .open({
            template: `<welcome-modal-v1 close='$close()' plan=${account.edition.planId}></welcome-modal-v1>`,
            windowClass: "large vertical-center",
          })
          .result.then(() => {
            this.expandAll();
          });
      }
    }
  };

  private applyWSChanges = (wsData: IGoalsMetricsData) => {
    if (!this.itemsData || !this.selectedSessionIds.includes(wsData.sessionId)) {
      return;
    }

    const updatedGoals: IAlignmentTreeGoal[] = [];
    const updatedMetrics: IAlignmentTreeMetric[] = [];

    // since only numeric properties are updated via the websocket it's safe to directly apply the update to the goal map without calling this.addGoals()
    (wsData.goals || []).forEach((goalUpdate) => {
      const goal = this.goalsById[goalUpdate.id];

      if (goal) {
        goal.attainment = goalUpdate.attainment;
        goal.fullAggregatedAttainment = goalUpdate.fullAggregatedAttainment;

        updatedGoals.push(goal);
      }
    });

    (wsData.metrics || []).forEach((metricUpdate) => {
      const goal = this.goalsById[metricUpdate.goalId];
      const metric = goal && Array.isArray(goal.metrics) ? goal.metrics.find((m) => m.id === metricUpdate.id) : null;
      if (metric) {
        metric.actual = metricUpdate.actual;
        metric.attainment = metricUpdate.attainment;

        updatedMetrics.push(metric);
      }
    });

    this.itemsTreeService.applyWSChanges(this.itemsData, updatedGoals, updatedMetrics);

    this.renderTree();
  };

  // TODO: Delete when release use-indirect-metric-cascade-type
  private addSlice = (args: { cascading: IMetric; contributing: IMetric }) => {
    const contributingMetricParentGoal = this.goalsById[args.contributing.goalId];
    const cascadingMetricParentGoal = this.goalsById[args.cascading.goalId];

    if (!contributingMetricParentGoal.metrics) {
      contributingMetricParentGoal.metrics = [];
    }

    if (!contributingMetricParentGoal.metrics.find((m) => m.id === args.contributing.id)) {
      contributingMetricParentGoal.metrics.push(args.contributing as IAlignmentTreeMetric);
    }

    cascadingMetricParentGoal.metrics.find((metric) => metric.id === args.cascading.id).links = args.cascading.links;

    this.itemsTreeService.updateMetricNode(this.itemsData, args.cascading as IAlignmentTreeMetric, cascadingMetricParentGoal, this.generateAdditionalData());

    this.renderTree();
  };

  private constructTree = (): IPromise<IAlignmentTreeFilterResult> => {
    let goalsRequest: IRestLayerRequest;

    const hasNonSessionFilters = this.filters.filter((x) => x.fieldName !== "sessionId" && hasFilterValue(x)).length > 0;
    if (hasNonSessionFilters) {
      const filtersOverride: Record<string, RestLayerFilterFactory> = {
        ...filtersOverrideForTagTitle(),
        ...filtersOverrideForOkrName(),
      };

      const separatedFilterValues: IGridFilter[] = separateDynamicValues(angular.copy(this.filters));
      goalsRequest = toRestLayerRequest({ filters: separatedFilterValues, filtersOverride });
    }

    let filterPromise: IPromise<IAlignmentTreeFilterResult>;
    if (goalsRequest) {
      goalsRequest.fields = "_id";
      goalsRequest.limit = -1;

      filterPromise = this.goalService.getGoalsV2(goalsRequest).then((matches) => ({ goalIds: matches.items.map((x) => x.id) }));
    } else {
      filterPromise = this.$q.resolve<IAlignmentTreeFilterResult>(undefined);
    }

    return filterPromise.then((filterResult) => {
      this.itemsData = this.itemsTreeService.constructItemsTreeFromOkrs(
        Object.values(this.goalsById),
        { filterResult, sharedId: this.sharedId, sharedParentId: this.sharedParentId, locateGoalId: this.locateId },
        this.generateAdditionalData(),
        {
          isReorderGoalChildrenFeatureEnabled: this.isReorderGoalChildrenFeatureEnabled,
          isNestedViewV2Enabled: this.isNestedViewV2Enabled,
        }
      );
      return filterResult;
    });
  };

  private renderTree = (): void => {
    this.treeRenderer.render(this.itemsData?.tree, this.treeContext);
  };

  private get treeContext(): ITreeContext {
    return {
      areFiltersApplied: hasFiltersWithValues(this.filters) && !hasOnlySessionFilter(this.filters),
      isMetricCascadeTypeFeatureEnabled: this.isMetricCascadeTypeFeatureEnabled,
    };
  }

  private constructTreeAndRender = (): IPromise<unknown> => {
    return this.constructTree().then(() => {
      this.renderTree();
    });
  };

  private constructTreeAndRenderAndShowSnackbar = (params: { action: string; goalId: string; metricId?: string }): IPromise<unknown> => {
    return this.constructTreeAndRender().then(() => {
      if (hasFiltersWithValues(this.filters) && params && !hasOnlySessionFilter(this.filters)) {
        this.showAlignmentSnackbarNotification(params);
      }
    });
  };

  private unlink = (item: IItem): void => {
    this.$rootScope.traceAction("unlink_metric", () => {
      if (!isContributingMetricItem(item)) {
        return;
      }

      const link = item.crossContributorLinks.find((link) => link.direction === "outbound");

      this.metricLinkService.unlink(item.parent.id, link.id, "cascading", item.id).then(
        () => {
          this.okrEvents.broadcastGoalsChanged("goalChanged");
          this.$rootScope.$broadcast("goalsChanged");
        },
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
    });
  };

  private itemRearrange = (args: IItemRearrangeEventArgs): void => {
    this.$rootScope.traceAction("rearrange_item", () => {
      const newParentId = args.newParent.id;
      const newParentSessionId = args.newParent.session.id;

      // check if allowed to drop
      // if the session is the same we skip checks
      if (newParentSessionId !== args.item.session.id && newParentSessionId !== this.sessionIdMap[args.item.session.id].parentId) {
        this.$uibModal.open({
          templateUrl: require("../../../goals/views/help/cycle.jade"),
          windowClass: "medium",
        });
        return;
      }

      const goal = this.goalsById[args.item.id];
      if (!goal) {
        console.warn("Cannot find goal when opening a context menu", { id: args.item.id });
        return;
      }

      const updateGoal = () => {
        this.$scope.indicators.updating = true;

        this.goalService
          .patchGoal(args.item.id, { parentId: newParentId }, { includeMetrics: true })
          .catch((error) => this.uiErrorHandlingService.handleModal(error))
          .finally(() => (this.$scope.indicators.updating = false));
      };

      const contributingMetrics = (goal.metrics || []).filter(isContributingMetric);
      if (contributingMetrics.length > 0) {
        this.showBreakMetricLinksWarning(contributingMetrics, updateGoal);
      } else {
        updateGoal();
      }
    });
  };

  private itemTitleClick = (item: IItem): void => {
    if (item.type === "goal") {
      this.$state.go("._goalSidebar.goal", { id: item.id });
    } else if (item.type === "metric") {
      this.$state.go("._goalSidebar.goal._metric.metric", { id: item.parent.id, metricId: item.id });
    }
  };

  private contextMenuOpen = (evt: MouseEvent, item: IItem): void => {
    this.$scope.contextMenuItem = item;

    // run in the next digest cycle so that all binding in the menu are applied correctly
    this.$timeout(() => {
      const contextMenuEl = document.getElementById("context-menu");
      const dropDownWidth = parseInt(contextMenuEl.style.minWidth, 10);
      const distanceToRightScreenEnd = window.innerWidth - evt.pageX - dropDownWidth;
      const offsetLeft = distanceToRightScreenEnd < 0 ? dropDownWidth : 0;

      const left = evt.pageX - this.getLeftNavWidth() - 30;
      let top = evt.pageY;
      contextMenuEl.style.left = `${left - offsetLeft}px`;
      contextMenuEl.style.top = `${top}px`;

      // make sure the drop-down is not off vertically
      const space = 10; // leave some room on the bottom

      // this includes the area between the top navigation and the alignment sidebar
      const { clientHeight: totalHeight } = document.getElementById("alignment-tree-holder");

      // these is drop-down's size
      const { clientHeight } = contextMenuEl;

      const isOffVertically = top + clientHeight + space >= totalHeight;
      if (isOffVertically) {
        top -= clientHeight + space;
        contextMenuEl.style.top = `${top}px`;
      }
    });
  };

  private closeContextMenu = (): void => {
    // run in the next digest cycle so that all binding in the menu are applied correctly
    this.$timeout(() => {
      delete this.$scope.contextMenuItem;
    });
  };

  private additionalOwnersOpen = (evt: MouseEvent, datum: AdditionalOwnersOpenEventDatum): void => {
    this.$scope.additionalOwners = datum.additionalOwners;
    // run in the next digest cycle so that all binding in the menu are applied correctly
    this.$timeout(() => {
      const dropDownWidth = 190;
      const offsetLeft = dropDownWidth / 2;
      const left = datum.left - this.getLeftNavWidth() - offsetLeft;

      const additionalOwnersMenuEl = document.getElementById("additional-owners-menu");
      additionalOwnersMenuEl.style.left = `${left}px`;
      additionalOwnersMenuEl.style.top = `${datum.top}px`;
    });
  };

  private getLeftNavWidth = (): number => {
    if (this.$rootScope.newExperienceEnabledForUser) {
      if (this.$rootScope.hideMenuV2) {
        return NAV_WIDTH.collapsed;
      }
      return NAV_WIDTH.expanded;
    }

    const { left, width } = document.getElementById("left-navigation").getBoundingClientRect();
    return width + left;
  };

  private closeAdditionalOwnersMenu = (): void => {
    // run in the next digest cycle so that all binding in the menu are applied correctly
    this.$timeout(() => {
      delete this.$scope.additionalOwners;
    });
  };

  private toggleItemSupporters = (item: IItem): void => {
    this.itemsTreeService.toggleItemSupporters(item.id, this.itemsData, this.currentSessionId);
    this.treeRenderer.refresh();
  };

  private toggleItemSelf = (item: IItem): void => {
    this.itemsTreeService.toggleItemSelf(item.id, this.itemsData, this.currentSessionId);
    this.treeRenderer.refresh();
  };

  private showHiddenChildItems = (parent: IItem | null): void => {
    this.itemsTreeService.showHiddenChildItems(parent, this.itemsData);
    this.treeRenderer.refresh();
  };

  private confirmDeleteMetric = (goalId: string, metricId: string): void => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../../../goals/views/confirm-delete-metric.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope: IConfirmDeleteMetricScope) {
          $scope.indicators = { deleting: false };

          $scope.confirm = () => {
            $scope.indicators.deleting = true;
            ctrl.$rootScope.traceAction("delete_metric", () => {
              ctrl.deleteMetric(goalId, metricId).then(
                () => $scope.$close(),
                (error) => ctrl.uiErrorHandlingService.handleIndicator(error, { indicator: "deleting", inline: true })
              );
            });
          };
        },
      ],
    });
  };

  private confirmDeleteGoal = (goalId: string) => {
    const goal = this.goalsById[goalId];

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../../../goals/views/confirm-delete-goal.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope: IConfirmDeleteGoalScope) {
          $scope.linkedMetrics = (goal.metrics || []).filter((m) => isCascadingMetric(m) || isContributingMetric(m));

          $scope.confirm = () => {
            ctrl.$rootScope.traceAction("delete_goal", () => {
              // todo: we need some waiting indicator here?
              ctrl.deleteGoal(goal);
            });
            $scope.$close();
          };
        },
      ],
    });
  };

  private deleteMetric = (goalId: string, metricId: string): IPromise<void> => {
    return this.metricService.deleteMetric(goalId, metricId).then(() => {
      this.okrEvents.broadcastGoalsChanged("goalChanged");
      this.$rootScope.$broadcast("goalsChanged");
      this.okrEvents.broadcastGoalsChanged("metricDeleted", {
        goalId,
        metricId,
      });
    });
  };

  private deleteGoal = (goal: IAlignmentTreeGoal): void => {
    // case: the goals has children, we move all children one level up
    const orphans = this.goalIdsByParent[goal.id] || [];
    const orphanPromises = orphans.map((orphanId) => {
      // todo: this is problematic. It updates the whole goal with a stale version
      // this should be done on the backend
      // also the deletion and the updates are in parallel which means you can rebind the tree with
      // an unfinished update version.
      const goalObjectToPatch = {
        parentId: goal.parentId,
      };

      return this.goalService.patchGoal(orphanId, goalObjectToPatch, { includeMetrics: true });
    });

    this.$q
      .all(orphanPromises)
      .then(() => this.goalService.deleteGoal(goal.id))
      .then(
        () => this.$rootScope.$broadcast("goalsChanged"),
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
  };

  private openCloneModal = (goalId: string): void => {
    this.$rootScope.traceAction("open_clone_modal", () => {
      this.getGoalForClone(goalId).then(
        (goal) => {
          this.cloneOkrsModal.openForGoalClone(goal);
        },
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
    });
  };

  private openCloneMetricModal = (metricId: string): void => {
    this.metricService
      .getMetric(metricId)
      .then((metric) => {
        this.cloneOkrsModal.openForMetricClone(metric);
      })
      .catch((err) => this.uiErrorHandlingService.handleModal(err));
  };

  private openDisableSyncModal = (metricId: string): void => {
    this.disableMetricSyncModal.open(metricId);
  };

  private openMoveModal = (goalId: string): void => {
    this.$rootScope.traceAction("open_move_modal", () => {
      this.getGoalForClone(goalId).then(
        (goal) => this.goalModalsService.openModalForMove("goal", goal),
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
    });
  };

  private getGoalForClone = (goalId: string): IPromise<IGoal> => {
    // here we want to get the whole goal before cloning/moving because we want to be exactly the same
    // the reason not to change field projection in the alignment is that we need the whole goal only when
    // we are cloning it - which is more "rare" operation and we don't need this whole data for, let's say 400 goals
    // in the alignment tree
    return this.goalService.getGoalWithFields(goalId);
  };

  private getShareableUrl = (itemId: string): void => {
    const currentUrl = window.location.href;
    const hasQueryString = currentUrl.indexOf("?") >= 0;
    const sharedId = `sharedId=${itemId}`;
    copyToClipboard(currentUrl + (hasQueryString ? "&" : "?") + sharedId, { container: getCopyURLNotificationEl(), duration: 3000 });
  };

  private closeShareableScreen = (): void => {
    this.$scope.shareableScreenShown = false;
  };

  private centerTreeToSharedItem = (): void => {
    const goalItem = this.getSharedGoalItemId();
    const item = this.getSharedItemId();
    const idx = this.getSharedItemIdx(goalItem.id, item.id);
    this.sharedId = null;

    if (this.sharedParentId) {
      this.sharedParentId = null;
      this.$q.when(this.treeRenderer.scrollIntoView(goalItem)).then(() => {
        this.treeRenderer.setStyle(goalItem, { childSelector: "rect.pad", css: { property: "stroke", value: "#8d73d9" }, animation: { duration: 200 } });

        if (item.type === "goal") {
          this.$state.go("._goalSidebar.goal", { id: item.id });
        } else if (item.type === "metric") {
          this.$state.go("._goalSidebar.goal._metric.metric", { id: goalItem.id, metricId: item.id });
        }
      });
    } else {
      this.$q.when(this.treeRenderer.scrollIntoView(goalItem, idx)).then(() => this.showSharedItemOverlay(item));
    }
  };

  private getSharedGoalItemId = (): IItemId => {
    if (this.goalsById[this.sharedId]) {
      return { id: this.sharedId, type: "goal" };
    }

    const goalId = Object.entries(this.goalsById).find((goal) => goal[1].metrics?.some((m) => m.id === this.sharedId))[0];
    return { id: goalId, type: "goal" };
  };

  private getSharedItemId = (): IItemId => {
    if (this.goalsById[this.sharedId]) {
      return { id: this.sharedId, type: "goal" };
    }
    return { id: this.sharedId, type: "metric" };
  };

  private getSharedItemIdx = (goalId: string, itemId: string): number => {
    if (goalId === itemId) {
      return 0;
    }
    const sortedMetrics = [...this.goalsById[goalId].metrics].sort(byNumericField("orderId"));
    return sortedMetrics.findIndex((m) => m.id === itemId);
  };

  private locateItem = (): void => {
    const item: IItemId = { id: this.locateId, type: "goal" };
    this.$q.when(this.treeRenderer.scrollIntoView(item)).then(() => this.showLocatedItemOverlay(item));
  };

  // TODO GVS-12970 - remove this.showMessageOverlay when it's no longer in use
  private showSharedItemOverlay = (item: IItemId): void => {
    if (this.isShareableURLsFeatureEnabled) {
      const rect = this.treeRenderer.getNodeOverlayBoundingClientRect(item);

      this.showSharedItemOutline(item);
      locatedItemOverlayAnimation(rect, this.$transitions);
      return;
    }

    this.showMessageOverlay(item);
  };

  private showSharedItemOutline = (item: IItemId, duration = 200): void => {
    this.treeRenderer.setStyle(item, { childSelector: "rect.item-pad", css: { property: "stroke", value: "#8d73d9" }, animation: { duration } });
    this.treeRenderer.setStyle(item, { childSelector: "rect.item-pad", css: { property: "stroke-width", value: "4px" }, animation: { duration } });
  };

  // TODO GVS-12970 - remove this when it's no longer in use
  private showMessageOverlay = (item: IItemId): void => {
    const rect = this.treeRenderer.getNodeBoundingClientRect(item);
    const top = rect.top + 2;
    const height = rect.height - 4;
    const width = rect.width - 4;
    const left = rect.left + 2;
    const right = rect.right + 2;

    const overlayTopEl = document.getElementById("overlay-top");
    overlayTopEl.style.height = `${top}px`;

    const overlayMessageEl = document.getElementById("overlay-message");
    overlayMessageEl.style.top = `${top - 130}px`;
    overlayMessageEl.style.left = `${left + 273}px`;

    const overlayBottomEl = document.getElementById("overlay-bottom");
    overlayBottomEl.style.height = `${window.innerHeight - (top + height)}px`;

    const overlayLeftEl = document.getElementById("overlay-left");
    overlayLeftEl.style.width = `${left}px`;
    overlayLeftEl.style.height = `${height}px`;
    overlayLeftEl.style.top = `${top}px`;

    const overlayRightEl = document.getElementById("overlay-right");
    overlayRightEl.style.width = `${window.innerWidth - (left + width)}px`;
    overlayRightEl.style.height = `${height}px`;
    overlayRightEl.style.top = `${top}px`;

    const overlayGlowEl = document.getElementById("overlay-glow");
    overlayGlowEl.style.width = `${width}px`;
    overlayGlowEl.style.height = `${height}px`;
    overlayGlowEl.style.top = `${top}px`;
    overlayGlowEl.style.left = `${left}px`;

    // rotating overlay text and arrow on right side
    if (right > 1400) {
      overlayMessageEl.style.left = `${left - 300}px`;
      overlayMessageEl.style.top = `${top - 137}px`;

      document.querySelector<HTMLElement>("#overlay-message > .gh-mt-1").style.float = "right";
      document.querySelector<HTMLElement>("#overlay-message > .gh-mt-1").style.marginTop = "0px";

      document.querySelector<HTMLElement>("#overlay-message > img").style.transform = "rotateY(190deg)";
      document.querySelector<HTMLElement>("#overlay-message > img").style.left = "350px";
    }

    this.$scope.shareableScreenShown = true;
  };

  private showLocatedItemOverlay = (item: IItemId): void => {
    const rect = this.treeRenderer.getNodeBoundingClientRect(item);
    this.treeRenderer.setStyle(item, { childSelector: "rect.pad", css: { property: "stroke", value: "#8d73d9" }, animation: { duration: 200 } });
    locatedItemOverlayAnimation(rect, this.$transitions);
  };

  private makeRoot = (goalId: string): void => {
    this.$rootScope.traceAction("make_root", () => {
      const goal = this.goalsById[goalId];
      const contributingMetrics = (goal.metrics || []).filter(isContributingMetric);

      const updateGoal = () => {
        this.unpin();
        this.$scope.indicators.updating = true;

        this.goalService.patchGoal(goalId, { parentId: "" }, { includeMetrics: true }).finally(() => (this.$scope.indicators.updating = false));
      };

      if (contributingMetrics.length > 0) {
        this.showBreakMetricLinksWarning(contributingMetrics, updateGoal);
      } else {
        updateGoal();
      }
    });
  };

  private showBreakMetricLinksWarning = (metrics: IAlignmentTreeMetric[], callback: () => void) => {
    if (this.isMetricCascadeTypeFeatureEnabled) {
      // Remove 'warn-break-metric-links' modal and $timeout when feature is released
      // Added $timeout because the caller would not expect the callback to be called immediately.
      this.$timeout(() => callback());
      return;
    }

    this.$uibModal.open({
      templateUrl: require("../../../goals/views/warn-break-metric-links.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope: IBreakMetricLinksWarningScope) {
          $scope.metrics = metrics;

          $scope.confirm = () => {
            callback();
            $scope.$close();
          };
        },
      ],
    });
  };

  private zoomIn = (): void => {
    const newZoomRatio = this.$scope.zoomRatio + ratioStep;
    if (!this.$scope.useScrollbars || newZoomRatio > allowedZoomRange.max) {
      return;
    }

    this.$scope.zoomRatio = newZoomRatio;
    this.treeRenderer.setZoomRatio(this.$scope.zoomRatio).refresh();
  };

  private zoomOut = (): void => {
    const newZoomRatio = this.$scope.zoomRatio - ratioStep;
    if (!this.$scope.useScrollbars || newZoomRatio < allowedZoomRange.min) {
      return;
    }

    this.$scope.zoomRatio = newZoomRatio;
    this.treeRenderer.setZoomRatio(this.$scope.zoomRatio).refresh();
  };

  private resetZoom = (): void => {
    if (!this.$scope.useScrollbars) {
      return;
    }

    this.$scope.zoomRatio = 1;
    this.treeRenderer.setZoomRatio(this.$scope.zoomRatio).refresh();
  };

  private isGoalActionAllowed = (goalId: string, action: GoalAction): boolean => {
    return (this.allowedActionsByGoal[goalId] || []).indexOf(action) >= 0;
  };

  private isAnyGoalActionAllowed = (goalId: string, ...actions: GoalAction[]): boolean => {
    return actions.some((action) => this.isGoalActionAllowed(goalId, action));
  };

  private canHandleGoalItem = (item: IItem) => {
    if (!this.$rootScope.hasPermission("ManageGoals")) {
      return false;
    }

    if (item.type !== "goal") {
      return false;
    }

    if (item.locked) {
      return false;
    }

    if (item.obfuscated) {
      return false;
    }

    const { status } = this.sessionIdMap[item.session.id];
    if (status === "locked" || status === "closed") {
      return false;
    }

    return this.isGoalActionAllowed(item.id, "update");
  };

  private pin = (goalId: string): void => {
    this.$rootScope.traceAction("pin_goal", () => {
      const pinGoal = () => {
        if (this.pinnedItem) {
          this.pinnedItem.pinned = false;
        }

        this.pinnedItem = this.itemsData.idMap[goalId];
        this.$scope.pinnedItem = { id: goalId, title: this.pinnedItem.title };
        this.pinnedItem.pinned = true;
        this.treeRenderer.refresh();
      };

      const goal = this.goalsById[goalId];
      const contributingMetrics = (goal.metrics || []).filter(isContributingMetric);
      if (contributingMetrics.length > 0) {
        this.showBreakMetricLinksWarning(contributingMetrics, pinGoal);
      } else {
        pinGoal();
      }
    });
  };

  private moveHere = (itemId: string) => {
    this.$rootScope.traceAction("move_goal_here", () => {
      if (!this.pinnedItem) {
        return;
      }

      this.$scope.indicators.updating = true;

      this.goalService
        .patchGoal(this.pinnedItem.id, { parentId: itemId }, { includeMetrics: true })
        .then(
          () => this.unpin(),
          (error) => this.uiErrorHandlingService.handleModal(error)
        )
        .finally(() => (this.$scope.indicators.updating = false));
    });
  };

  private unpin = (): void => {
    this.$rootScope.traceAction("unpin_goal", () => {
      if (this.pinnedItem) {
        this.pinnedItem.pinned = false;
        delete this.pinnedItem;
        delete this.$scope.pinnedItem;
        this.treeRenderer.refresh();
      }
    });
  };

  private getGoalsForSessions(sessionIds: string[]): IPromise<IAlignmentTreeGoal[]> {
    if (!sessionIds.length) {
      return this.$q.resolve([]);
    }

    return this.goalService
      .getAllGoalsV2<IAlignmentTreeGoal>({ filter: { sessionId: { $in: sessionIds } }, fields: getGoalsForSessionsFields, limit: 500 })
      .then((collection) => collection.items);
  }

  private addChildObjective = (item: IItem): void => {
    this.$rootScope.traceAction("add_child_objective", () => {
      this.$state.go("._goalForm.form", { parentId: item.id, parentType: item.type, externalSessionId: item.session.id });
    });
  };

  private showAlignmentSnackbarNotification = (params: { action: string; goalId: string; metricId?: string }): void => {
    let isChangeVisible: boolean;

    const targetGoal = this.itemsData.idMap[params.goalId];
    const targetGoalParent = targetGoal && targetGoal.parent ? this.itemsData.idMap[targetGoal.parent.id] : null;

    switch (params.action) {
      case "goalCreated":
      case "goalPatched":
      case "goalUpdated":
      case "goalCloned":
      case "goalMoved":
      case "reviewApproved":
      case "reviewDeclined":
      case "reviewSentForApproval":
      case "reviewWithdrawn":
      case "metricLinked":
      case "metricUnlinked":
        isChangeVisible =
          targetGoal && (targetGoal.matchesFilter || (targetGoalParent && targetGoalParent.supportersExpanded && !targetGoalParent.hidden && !targetGoal.hidden));
        if (!isChangeVisible) {
          this.snackbarEvents.broadcastShowSnackbar("changes_saved_not_visible", {
            actionCb: () => {
              this.$state.go("._goalSidebar.goal", { id: params.goalId });
            },
            actionBtnContent: "view_changes",
            skipClose: true,
          });
        }
        break;

      case "goalDeleted":
        isChangeVisible =
          !targetGoalParent ||
          (targetGoalParent &&
            targetGoalParent.supportersExpanded &&
            targetGoalParent.supporters.filter((supporter) => supporter.id !== params.goalId).length &&
            !targetGoalParent.hidden);
        if (!isChangeVisible) {
          this.snackbarEvents.broadcastShowSnackbar("changes_saved_not_visible", { actionCb: () => null, actionBtnContent: "cancel", skipClose: true });
        }
        break;

      case "metricCreated":
      case "metricPatched":
      case "metricMoved":
        isChangeVisible =
          targetGoal && (targetGoal.matchesFilter || (targetGoalParent && targetGoalParent.supportersExpanded && !targetGoalParent.hidden && !targetGoal.hidden));
        if (!isChangeVisible) {
          this.snackbarEvents.broadcastShowSnackbar("changes_saved_not_visible", {
            actionCb: () => {
              this.$state.go("._goalSidebar.goal._metric.metric", { id: params.goalId, metricId: params.metricId });
            },
            actionBtnContent: "view_changes",
            skipClose: true,
          });
        }
        break;

      case "metricDeleted":
        isChangeVisible = !targetGoal || (targetGoal && targetGoal.supportersExpanded && !targetGoal.hidden);
        if (!isChangeVisible) {
          this.snackbarEvents.broadcastShowSnackbar("changes_saved_not_visible", { actionCb: () => null, actionBtnContent: "cancel", skipClose: true });
        }
        break;
    }
  };

  private canCascadeMetric = (metricItem: IItem): boolean => {
    const metric = this.goalsById[metricItem.parent.id].metrics.find((metric) => metric.id === metricItem.id);

    return canCascadeMetric(metric, this.isGoalAbandoned(metricItem));
  };

  private isGoalAbandoned = (metricItem: IItem): boolean => {
    const goalClosedStatus = this.goalsById[metricItem.parent.id].closedStatus;
    return isOkrStatusAbandoned(goalClosedStatus, { isCloseOkrFeatureEnabled: this.isCloseOkrFeatureEnabled });
  };

  private openCascadeMetricForm = (item: IItem): void => {
    const goal = this.goalsById[item.parent.id];
    const metric = goal?.metrics.find((metric) => metric.id === item.id);

    this.cascadingMetricFormModal.handleOpenCascadingForm(metric, goal?.name);
  };
}
